import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import api from "../../../utils/api";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../../../context/ToastContext";
import { openLoginModal } from "../../../../redux/authSlice";
import "./CasinoPage.css"; // Reuse casino styles
import {
  FaPlay,
  FaArrowLeft,
  FaInfoCircle,
  FaExclamationTriangle,
} from "react-icons/fa";

const GameLaunchPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [game, setGame] = useState(null);
  const [launchUrl, setLaunchUrl] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const location = useLocation();
  const prevUserIdRef = useRef(user?._id || null);

  // Reset any previously launched session whenever the logged-in user
  // changes (login, logout, or switching accounts). Without this, a
  // running iframe keeps playing after logout, and a newly logged-in
  // user can end up viewing the previous user's game session/balance.
  useEffect(() => {
    const currentUserId = user?._id || null;
    if (prevUserIdRef.current !== currentUserId) {
      prevUserIdRef.current = currentUserId;
      setLaunchUrl(null);
      setError(null);
    }
  }, [user]);

  // Scroll to top immediately when slug changes
  useLayoutEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",
    });
  }, [slug]);

  // 1. Fetch Game Details
  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/games/${slug}`);
        if (res.data.success && res.data.data) {
          setGame(res.data.data);
        } else {
          throw new Error("Game not found");
        }
      } catch (err) {
        console.error("Error fetching game details:", err);
        setError("Game not found or unavailable.");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchGameDetails();
    }
  }, [slug]);

  // Handle initial autoscroll to title
  useEffect(() => {
    if (location.state?.autoScroll && titleRef.current && !loading) {
      const yOffset = -6; //-70; // Account for header height if needed
      const element = titleRef.current;
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [loading, location.state]);

  const handleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    }
  };

  // Add Key listener for F to toggle fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if focus is on an input or textarea
      if (
        ["input", "textarea"].includes(
          document.activeElement?.tagName?.toLowerCase(),
        )
      )
        return;

      if (e.key === "f" || e.key === "F") {
        handleFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // 2. Handle Launch
  const handleLaunch = async () => {
    if (!user) {
      dispatch(openLoginModal());
      return;
    }

    if (!game) return;

    try {
      setLaunching(true);
      const launchRes = await api.post("/games/launch", { gameId: game._id });

      if (
        (launchRes.data.status || launchRes.data.success) &&
        launchRes.data.data &&
        launchRes.data.data.url
      ) {
        setLaunchUrl(launchRes.data.data.url);
        // Try to trigger fullscreen immediately (works if called from a click event)
        handleFullscreen();
      } else {
        throw new Error(launchRes.data.message || "Failed to launch game");
      }
    } catch (err) {
      console.error("Game launch error:", err);
      const msg =
        err.response?.data?.message || err.message || "Failed to launch game";
      setError(msg);
      showToast(msg, "danger");
    } finally {
      setLaunching(false);
    }
  };

  // Auto-launch if game detail is fetched and user is logged in
  useEffect(() => {
    if (game && user && !launchUrl && !launching && !error) {
      handleLaunch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, user]);

  // Auto-fullscreen when game starts
  useEffect(() => {
    if (launchUrl) {
      const timer = setTimeout(() => {
        if (!document.fullscreenElement) {
          handleFullscreen();
        }
      }, 600); // Wait for iframe/container to stabilize
      return () => clearTimeout(timer);
    }
  }, [launchUrl]);

  if (loading) {
    return (
      <div
        className="casino-page"
        style={{ minHeight: "100vh", background: "#0b0b0f" }}
      >
        <div
          className="container"
          style={{ paddingTop: "40px", paddingBottom: "80px" }}
        >
          <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
            {/* Meta Data Skeleton (Provider · Game Type) */}
            <div
              style={{
                display: "flex",
                gap: "15px",
                marginBottom: "10px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  height: "16px",
                  width: "100px",
                  background: "#1f1f23",
                  borderRadius: "4px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg, #1f1f23 25%, #2a2a2a 50%, #1f1f23 75%)",
                    backgroundSize: "200% 100%",
                    animation: "skeleton-loading 1.5s infinite linear",
                  }}
                />
              </div>
              <div
                style={{
                  height: "4px",
                  width: "4px",
                  borderRadius: "50%",
                  background: "#333",
                }}
              />
              <div
                style={{
                  height: "16px",
                  width: "80px",
                  background: "#1f1f23",
                  borderRadius: "4px",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(90deg, #1f1f23 25%, #2a2a2a 50%, #1f1f23 75%)",
                    backgroundSize: "200% 100%",
                    animation: "skeleton-loading 1.5s infinite linear",
                  }}
                />
              </div>
            </div>

            {/* Title Skeleton */}
            <div
              style={{
                height: "40px",
                width: "40%",
                background: "#1f1f23",
                borderRadius: "8px",
                marginBottom: "20px",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, #1f1f23 25%, #2a2a2a 50%, #1f1f23 75%)",
                  backgroundSize: "200% 100%",
                  animation: "skeleton-loading 1.5s infinite linear",
                }}
              />
            </div>

            {/* Game Player Skeleton */}
            <div
              className="game-player-container skeleton-loading"
              style={{
                borderRadius: "16px",
                marginBottom: "30px",
              }}
            ></div>
          </div>
        </div>
        <style>{`
                    @keyframes skeleton-loading {
                        0% { background-position: 200% 0; }
                        100% { background-position: -200% 0; }
                    }
                `}</style>
      </div>
    );
  }

  if (!game) {
    return <ErrorState error={error} navigate={navigate} />;
  }

  // Render Game Details
  return (
    <div
      className="casino-page"
      style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}
    >
      {/* Background Blur */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${game.thumbnail || "/images/casino-bg.jpg"})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(20px) brightness(0.3)",
          zIndex: 0,
        }}
      />

      <div
        className="container"
        style={{
          position: "relative",
          zIndex: 1,
          paddingTop: "40px",
          paddingBottom: "80px",
        }}
      >
        {/* Game Info Above Screen */}
        <div style={{ maxWidth: "1000px", margin: "0 auto 20px" }}>
          <div
            style={{
              display: "flex",
              gap: "15px",
              alignItems: "center",
              fontSize: "14px",
              marginBottom: "8px",
            }}
          >
            <span
              style={{
                color: "#ff9900",
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            >
              {game.provider?.name}
            </span>
            <span
              style={{
                width: "4px",
                height: "4px",
                background: "#555",
                borderRadius: "50%",
              }}
            ></span>
            <span style={{ color: "#ccc" }}>
              {game.gameType
                ? game.gameType
                    .split("-")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : "Casino Game"}
            </span>
          </div>
          <h1
            ref={titleRef}
            style={{ fontSize: "2rem", fontWeight: "bold", color: "white" }}
          >
            {game.name}
          </h1>
        </div>

        {/* Game Player Container */}
        <div
          ref={containerRef}
          className="game-player-container"
          style={{
            borderRadius: isFullscreen ? "0" : "16px",
          }}
        >
          {/* Top Control Bar */}
          {!isFullscreen && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#2a2a2a",
                padding: "6px 12px",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {/* Left Side: Stop Game Button (only when playing) */}
              <div>
                {launchUrl && (
                  <button
                    onClick={() => setLaunchUrl(null)}
                    className="hover-scale"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      color: "#ccc",
                      border: "none",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    Stop Game
                  </button>
                )}
              </div>

              {/* Right Side: Maximize Button */}
              <button
                className="hover-scale"
                onClick={handleFullscreen}
                style={{
                  background: "rgba(255,255,255,0.1)",
                  border: "none",
                  color: "#ccc",
                  width: "28px",
                  height: "28px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                title="Fullscreen"
              >
                <svg
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  height="14"
                  width="14"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                </svg>
              </button>
            </div>
          )}

          {/* Main Game Area - Centered Play Button */}
          {/* Main Game Area */}
          {launchUrl ? (
            <div
              style={{
                flex: 1,
                background: "#0b0b0f", // Match site bg
                position: "relative",
              }}
            >
              <iframe
                src={launchUrl}
                title={game.name}
                width="100%"
                height="100%"
                frameBorder="0"
                allowFullScreen
                style={{ display: "block" }}
              />
            </div>
          ) : error ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                background: "#0b0b0f",
                padding: "20px",
                textAlign: "center",
              }}
            >
              <FaExclamationTriangle
                size={40}
                color="#ff4d4d"
                style={{ marginBottom: "15px" }}
              />
              <h3 style={{ color: "white", marginBottom: "10px" }}>
                Launch Failed
              </h3>
              <p
                style={{
                  color: "#889",
                  fontSize: "0.9rem",
                  maxWidth: "80%",
                  marginBottom: "20px",
                }}
              >
                {error}
              </p>
              <button
                onClick={() => {
                  setError(null);
                  handleLaunch();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  border: "none",
                  padding: "8px 20px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Retry Launch
              </button>
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                backgroundImage: `url(${game.thumbnail})`, // Use thumbnail as faint background
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              {/* Overlay to darken thumbnail */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background: "rgba(11, 11, 15, 0.9)", // Match site bg color with opacity
                  backdropFilter: "blur(5px)",
                }}
              ></div>

              {/* Play Button */}
              <button
                onClick={handleLaunch}
                disabled={launching}
                className="hover-scale"
                style={{
                  position: "relative",
                  zIndex: 10,
                  background:
                    "linear-gradient(90deg, #ff9900 0%, #ff5e00 100%)",
                  color: "white",
                  border: "none",
                  padding: "12px 30px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "6px",
                  cursor: launching ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  boxShadow: "0 5px 15px rgba(255, 153, 0, 0.4)",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
              >
                {launching ? (
                  <>Loading...</>
                ) : (
                  <>
                    <FaPlay size={14} /> Play Now
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Description Below */}
        {game.description && (
          <div
            style={{ maxWidth: "1000px", margin: "30px auto 0", color: "#ccc" }}
          >
            <div
              style={{
                marginTop: "20px",
                fontSize: "0.9rem",
                lineHeight: "1.6",
              }}
            >
              {game.description}
            </div>
          </div>
        )}
      </div>

      {/* Global Styles for Hover Effects */}
      <style>{`
                .hover-scale:hover {
                    transform: scale(1.05);
                    filter: brightness(1.1);
                }
                .hover-scale:active {
                    transform: scale(0.95);
                }
            `}</style>
    </div>
  );
};

export default GameLaunchPage;

const ErrorState = ({ error, navigate }) => (
  <div
    className="casino-page error"
    style={{
      // minHeight: "85vh",
      background: "#0b0b0f",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      paddingTop: "60px",
      paddingBottom: "60px",
    }}
  >
    <div
      style={{
        maxWidth: "500px",
        width: "100%",
        margin: "0 auto",
        position: "relative",
      }}
    >
      {/* Subtle Glow Background */}
      {/* <div style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "300px",
                height: "300px",
                background: "radial-gradient(circle, rgba(255, 153, 0, 0.15) 0%, transparent 70%)",
                zIndex: 0,
                pointerEvents: "none"
            }} /> */}

      <div style={{ position: "relative", zIndex: 1 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "80px",
            height: "80px",
            background:
              "linear-gradient(135deg, rgba(255,77,77,0.1) 0%, rgba(255,77,77,0.05) 100%)",
            borderRadius: "20px",
            marginBottom: "30px",
            border: "1px solid rgba(255,77,77,0.2)",
            boxShadow: "0 10px 30px rgba(255, 77, 77, 0.1)",
          }}
        >
          <FaExclamationTriangle size={32} color="#ff4d4d" />
        </div>

        <h1
          style={{
            fontSize: "2.5rem",
            fontWeight: "800",
            marginBottom: "15px",
            color: "white",
            letterSpacing: "-0.5px",
          }}
        >
          Game Unavailable
        </h1>

        <p
          style={{
            color: "#889",
            fontSize: "1.1rem",
            marginBottom: "40px",
            lineHeight: "1.6",
          }}
        >
          {error ||
            "The game you are looking for seems to be missing. It may have been removed or is temporarily unavailable."}
        </p>

        {/* <button
                    onClick={() => navigate("/casino")}
                    className="hover-scale-button"
                    style={{
                        padding: "16px 40px",
                        background: "#333",
                        border: "none",
                        color: "white",
                        borderRadius: "12px",
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "10px",
                        boxShadow: "0 5px 15px rgba(0,0,0,0.3)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                >
                    <FaArrowLeft /> Back to Casino
                </button> */}
      </div>
    </div>
    <style>{`
            .hover-scale-button:hover {
                transform: translateY(-2px);
                box-shadow: 0 12px 25px rgba(255, 153, 0, 0.4) !important;
            }
            .hover-scale-button:active {
                transform: translateY(0);
            }
        `}</style>
  </div>
);
